import React from 'react';

import ProductFirstPriorityDetails from '../../../../components/product-first-priority-details/product-first-priority-details';
import ProductListCardActions from '../product-list-card-actions/product-list-card-actions';
import ProductListCardDetails from '../product-list-card-details/product-list-card-details';
import ProductListReviewDetails from '../product-list-review-details/product-list-review-details';

import {
  useProductEntryDataLayerValues,
  useProductQuantity
} from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics';
import { getNormalizedProductType } from '../../../../shared/utils/product/product';
import { setSelectedProductAvailabilityStore } from '../../utils/product-list-page-multistore-utils';

import {
  BundleType,
  ProductData,
  ProductTypeEnum
} from '../../../../shared/types/graphql-types';
import { ProductItem } from '../../../../shared/types/product';

import './product-list-card.scss';

interface ProductListCardProps {
  isTreadwellTabSelected: boolean;
  onAddProductClick: (productItem: ProductItem) => void;
  onCheckInventoryClick: () => void;
  onViewProductClick: () => void;
  product: ProductData;
}

function ProductListCard(props: ProductListCardProps) {
  const {
    isTreadwellTabSelected,
    onAddProductClick,
    onCheckInventoryClick,
    onViewProductClick,
    product
  } = props;

  const normalizedProductType =
    getNormalizedProductType(product) || ProductTypeEnum.Tires;
  const { defaultQuantity, quantity, setQuantity, totalQuantity } =
    useProductQuantity({
      articleServiceType: product?.articleServiceType,
      bundleType: product?.rearProduct ? BundleType.StaggeredSet : undefined,
      productType: normalizedProductType
    });
  const productDetailUrl = product?.url || '';
  const hasTdsStoppingData =
    Boolean(product?.tdsData?.stopping?.dryRating) &&
    Boolean(product?.tdsData?.stopping?.wetRating);
  const hasTdsWinterData =
    Boolean(product?.tdsData?.winter?.snowHandlingRating) &&
    Boolean(product?.tdsData?.winter?.snowBrakeFeet);

  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();

  if (window.isPreRendering) {
    return <div styleName="container" />;
  }

  return (
    <div
      data-testid="product-list-card"
      id={product && product.code ? `product-code-${product.code}` : ''}
      styleName="container"
    >
      <ProductListCardDetails
        onCheckInventoryClick={onCheckInventoryClick}
        onViewProductClick={onViewProductClick}
        product={product}
        productDetailUrl={productDetailUrl}
        quantity={quantity}
      />

      <ProductListCardActions
        defaultQuantity={defaultQuantity}
        onAddProductClick={onAddProductClick}
        onQuantityChange={setQuantity}
        onViewProductClick={onViewProductClick}
        product={product}
        productDetailUrl={productDetailUrl}
        quantity={quantity}
        totalQuantity={totalQuantity}
      />

      {normalizedProductType === ProductTypeEnum.Tires &&
        (product?.recommendationData ||
        hasTdsStoppingData ||
        hasTdsWinterData ? (
          <ProductFirstPriorityDetails
            dataReportUrl={productDetailUrl}
            onClick={() => {
              trackAction(
                'product_view_pdl_data_click',
                getProductEntryDataLayerValues(product)
              );
              setSelectedProductAvailabilityStore(product);
            }}
            product={product}
          />
        ) : (
          <ProductListReviewDetails
            isTreadwellTabSelected={isTreadwellTabSelected}
            numberOfReviews={product?.reviewSummaryData?.totalCount || 0}
            onClickReadReviewsFn={() =>
              setSelectedProductAvailabilityStore(product)
            }
            product={product}
            productDetailUrl={productDetailUrl}
          />
        ))}
    </div>
  );
}

export default ProductListCard;
